/**
 * OrbitTourViewer — Sprint 33 (Feb 2026)
 * ============================================
 * Client for the /api/lots/{lot_id}/orbit-tour endpoints. Renders a
 * drag-to-rotate 360° viewer built from 4 HD sprites at yaw 0/90/180/270°,
 * plus an optional Sora-2 orbital video teaser.
 *
 * Props:
 *   - lotId (str, required)
 *   - projectId (str, required)  — used for authorization redirects only
 *   - baseGenerationId (str, optional) — reuse a specific finished job as
 *       the parametric source ; falls back to the latest completed one.
 *   - onCompleted (fn, optional) — fired when status flips to "completed".
 *
 * UX states:
 *   1. Idle (no tour)       → "Générer tour 360°" CTA + optional video toggle
 *   2. Running              → progress bar + best-effort sprite thumbnail row
 *   3. Completed            → drag-rotate viewer + "▶ Play video Sora" if available
 *   4. Failed               → error banner + retry CTA
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PlayCircle, RotateCw, Sparkles, XCircle, ZoomIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 3500;
const YAW_STEPS = [0, 90, 180, 270];

const spriteUrl = (jobId, yaw) =>
  `${API}/orbit-tour/${jobId}/sprites/${yaw}?_=${jobId}`;
const videoUrl = (jobId) => `${API}/orbit-tour/${jobId}/video`;
const facadeCloseupUrl = (jobId) => `${API}/orbit-tour/${jobId}/facade-closeup?_=${jobId}`;


const OrbitTourViewer = ({ lotId, projectId, baseGenerationId, onCompleted }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [wantVideo, setWantVideo] = useState(false);
  const [wantFacadeCloseup, setWantFacadeCloseup] = useState(false);
  const [yaw, setYaw] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(false);
  const [showFacadeCloseup, setShowFacadeCloseup] = useState(false);
  const pollRef = useRef(null);

  // ─── Fetch latest tour once + resume polling if in-flight ──────────
  const fetchLatest = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/lots/${lotId}/orbit-tour`,
        { withCredentials: true },
      );
      setJob(data || null);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error('Impossible de charger la tour 360°');
    } finally { setLoading(false); }
  }, [lotId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  // Poll while pending / running.
  useEffect(() => {
    if (!job || ['completed', 'failed'].includes(job.status)) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (job?.status === 'completed' && onCompleted) onCompleted(job);
      return () => {};
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API}/orbit-tour/${job.orbit_job_id}`,
          { withCredentials: true },
        );
        setJob(data);
      } catch { /* keep polling */ }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [job, onCompleted]);

  const launch = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/lots/${lotId}/orbit-tour`,
        {
          generate_video: wantVideo,
          generate_facade_closeup: wantFacadeCloseup,
          base_generation_id: baseGenerationId || null,
        },
        { withCredentials: true },
      );
      setJob(data);
      toast.success('Tour 360° lancée — les 4 vues HD se préparent…');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de lancer la tour');
    } finally { setCreating(false); }
  };

  // ─── Drag-to-rotate handler ────────────────────────────────────────
  const dragState = useRef({ dragging: false, startX: 0, startYaw: 0 });
  const onPointerDown = (e) => {
    dragState.current = { dragging: true, startX: e.clientX, startYaw: yaw };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const stepPx = 60; // 60 px drag ⇒ 90° step
    const stepIdx = Math.round(dx / stepPx);
    const nextYaw = ((dragState.current.startYaw + stepIdx * 90) % 360 + 360) % 360;
    if (nextYaw !== yaw) setYaw(nextYaw);
  };
  const onPointerUp = (e) => {
    dragState.current.dragging = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const sprites = useMemo(() => (job?.sprites || []), [job]);
  const spriteByYaw = useMemo(() => {
    const m = {};
    sprites.forEach((s) => { m[s.yaw_deg] = s; });
    return m;
  }, [sprites]);
  const hasSprite = (yv) => !!spriteByYaw[yv];
  const hasVideo = job?.status === 'completed' && !!job.video_storage_path;
  const hasFacadeCloseup = job?.status === 'completed' && !!job.facade_closeup_storage_path;

  // ─── Render states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm p-4"
           data-testid="orbit-tour-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  const idle = !job || (job.status === 'failed' && !creating);
  if (idle) {
    return (
      <div className="p-6 border border-dashed border-white/15 rounded-lg bg-black/30 space-y-4"
           data-testid="orbit-tour-idle">
        <div className="flex items-center gap-2 text-white/90">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <h3 className="font-semibold text-sm">Tour 360° hybride</h3>
        </div>
        <p className="text-[12.5px] text-gray-400 leading-relaxed">
          Génère 4 rendus HD à 90° d&rsquo;écart (0°/90°/180°/270°) avec chaînage
          image-to-image pour garantir la cohérence des matériaux entre les
          vues. Optionnellement, un clip Sora 2 orbital 4 s peut être ajouté
          en teaser vidéo.
        </p>
        <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 cursor-pointer">
          <input type="checkbox" checked={wantVideo}
                 onChange={(e) => setWantVideo(e.target.checked)}
                 className="accent-amber-500 h-3.5 w-3.5"
                 data-testid="orbit-tour-video-toggle" />
          <span>Ajouter la vidéo Sora 2 orbitale (4 s, ~3-5 min de calcul)</span>
        </label>
        <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 cursor-pointer">
          <input type="checkbox" checked={wantFacadeCloseup}
                 onChange={(e) => setWantFacadeCloseup(e.target.checked)}
                 className="accent-amber-500 h-3.5 w-3.5"
                 data-testid="orbit-tour-facade-toggle" />
          <span>Ajouter le gros plan façade œil-d&rsquo;homme (~1.6 m, +1 rendu HD)</span>
        </label>
        {job?.status === 'failed' && (
          <div className="text-[11.5px] text-rose-300 flex items-start gap-1.5 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
            <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span data-testid="orbit-tour-error">{job.error || 'Échec précédent — relance possible'}</span>
          </div>
        )}
        <button
          onClick={launch}
          disabled={creating}
          data-testid="orbit-tour-launch-btn"
          className="inline-flex items-center gap-2 h-9 px-4 rounded bg-amber-500/90 hover:bg-amber-500 text-black font-semibold text-[12.5px] disabled:opacity-50 disabled:cursor-wait">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
          {creating ? 'Lancement…' : 'Générer la tour 360°'}
        </button>
      </div>
    );
  }

  const isRunning = ['pending', 'generating_sprites', 'generating_video', 'generating_facade_closeup'].includes(job.status);
  if (isRunning) {
    const done = sprites.length;
    const total = 4;
    const pct = Math.round((done / total) * 100);
    return (
      <div className="p-6 border border-amber-500/30 bg-amber-500/5 rounded-lg space-y-3"
           data-testid="orbit-tour-running">
        <div className="flex items-center gap-2 text-amber-200 text-sm font-semibold">
          <Loader2 className="w-4 h-4 animate-spin" />
          {job.status === 'generating_video' ? 'Génération vidéo Sora 2…'
           : job.status === 'generating_facade_closeup' ? 'Rendu gros plan façade œil-d\u2019homme…'
           : `Rendu HD ${done}/${total} sprites…`}
        </div>
        <div className="h-1.5 bg-white/10 rounded overflow-hidden">
          <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {YAW_STEPS.map((y) => (
            <div key={y} className="aspect-square rounded border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
              {hasSprite(y)
                ? <img src={spriteUrl(job.orbit_job_id, y)} alt={`Yaw ${y}°`} className="w-full h-full object-cover" />
                : <span className="text-[10px] text-gray-500">{y}°</span>}
            </div>
          ))}
        </div>
        <div className="text-[10.5px] text-gray-400">
          Job {job.orbit_job_id} · {job.progress || job.status}
        </div>
      </div>
    );
  }

  // Completed
  const active = spriteByYaw[yaw];
  return (
    <div className="space-y-3" data-testid="orbit-tour-completed">
      <div className="relative rounded-lg overflow-hidden bg-black border border-white/10 select-none"
           style={{ aspectRatio: '16/10', cursor: 'grab' }}
           onPointerDown={onPointerDown}
           onPointerMove={onPointerMove}
           onPointerUp={onPointerUp}
           data-testid="orbit-tour-canvas">
        {active
          ? <img
              src={spriteUrl(job.orbit_job_id, yaw)}
              alt={`Vue orbitale ${yaw}°`}
              draggable={false}
              className="w-full h-full object-cover"
              data-testid={`orbit-tour-sprite-${yaw}`}
            />
          : <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">Sprite {yaw}° manquant</div>}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 text-amber-300 text-[10px] font-mono">
          {yaw.toString().padStart(3, '0')}°
        </div>
        {/* Sprint 33 Phase 2 — Hotspot central cliquable pour ouvrir le
            gros plan façade œil-d'homme. */}
        {hasFacadeCloseup && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowFacadeCloseup(true); }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Zoom façade œil-d'homme (~1.6 m)"
            data-testid="orbit-tour-facade-hotspot"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                       h-12 w-12 rounded-full bg-amber-500/85 hover:bg-amber-500
                       text-black flex items-center justify-center shadow-2xl
                       ring-4 ring-amber-500/30 hover:ring-amber-500/50
                       transition-all hover:scale-110 animate-pulse"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {YAW_STEPS.map((y) => (
            <button
              key={y}
              onClick={() => setYaw(y)}
              data-testid={`orbit-tour-yaw-${y}`}
              className={`h-7 w-11 rounded text-[10.5px] font-mono border ${
                y === yaw
                  ? 'bg-amber-500 text-black border-amber-500 font-semibold'
                  : 'border-white/15 text-white/70 hover:border-white/40'
              }`}
            >{y}°</button>
          ))}
        </div>
        {hasVideo && (
          <button
            onClick={() => setPlayingVideo(true)}
            data-testid="orbit-tour-play-video"
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded bg-rose-500/90 hover:bg-rose-500 text-white text-[11px] font-semibold">
            <PlayCircle className="w-3.5 h-3.5" />
            Vidéo Sora
          </button>
        )}
      </div>
      {playingVideo && hasVideo && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
             onClick={() => setPlayingVideo(false)}
             data-testid="orbit-tour-video-modal">
          <video
            src={videoUrl(job.orbit_job_id)}
            autoPlay controls loop
            className="max-w-4xl w-full rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {job.video_error && (
        <div className="text-[10.5px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
          Vidéo Sora indisponible : {job.video_error}
        </div>
      )}
      {job.facade_closeup_error && (
        <div className="text-[10.5px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5"
             data-testid="orbit-tour-facade-error">
          Gros plan façade indisponible : {job.facade_closeup_error}
        </div>
      )}
      {showFacadeCloseup && hasFacadeCloseup && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
             onClick={() => setShowFacadeCloseup(false)}
             data-testid="orbit-tour-facade-modal">
          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={facadeCloseupUrl(job.orbit_job_id)}
              alt="Gros plan façade œil-d'homme"
              className="w-full rounded shadow-2xl"
              data-testid="orbit-tour-facade-image"
            />
            <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/80 text-amber-300 text-[11px] font-mono">
              Façade œil-d&apos;homme · ~1.60 m
            </div>
            <button
              onClick={() => setShowFacadeCloseup(false)}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
              data-testid="orbit-tour-facade-close"
              aria-label="Fermer">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrbitTourViewer;
